import { NavLink, Outlet } from "react-router-dom";
import {
  InboxIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  CalculatorIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import {
  InboxIcon as InboxSolid,
  ArchiveBoxIcon as ArchiveSolid,
  ChartBarIcon as ChartSolid,
  Cog6ToothIcon as CogSolid,
  ClockIcon as ClockSolid,
  BanknotesIcon as BanknotesSolid,
  CalculatorIcon as CalculatorSolid,
  ClipboardDocumentCheckIcon as PrueflisteSolid,
} from "@heroicons/react/24/solid";
import clsx from "clsx";
import { useMe, useLogout } from "../lib/auth";

// Track G / Variante 1 (2026-06-05): AxisTree ist NICHT mehr in der Sidebar.
// Er lebt jetzt als Sub-Bereich der Auswertungen-Page (kontextuelle Sichtbarkeit
// dort, wo er nützt). Sidebar / Bottom-Tab sind auf reine Navigation reduziert.

type NavItem = {
  to: string;
  label: string;
  Icon: typeof InboxIcon;
  IconActive: typeof InboxIcon;
  nurDesktop?: boolean; // am Handy nicht in der unteren Leiste (Platz), dort über einen Link erreichbar
};

const NAV: NavItem[] = [
  { to: "/inbox", label: "Inbox", Icon: InboxIcon, IconActive: InboxSolid },
  { to: "/zuletzt", label: "Zuletzt", Icon: ClockIcon, IconActive: ClockSolid },
  { to: "/archiv", label: "Archiv", Icon: ArchiveBoxIcon, IconActive: ArchiveSolid },
  { to: "/buchungen", label: "Buchungen", Icon: BanknotesIcon, IconActive: BanknotesSolid },
  { to: "/steuer", label: "Steuer", Icon: CalculatorIcon, IconActive: CalculatorSolid, nurDesktop: true },
  { to: "/pruefliste", label: "Prüfliste", Icon: ClipboardDocumentCheckIcon, IconActive: PrueflisteSolid, nurDesktop: true },
  { to: "/auswertungen", label: "Auswertungen", Icon: ChartBarIcon, IconActive: ChartSolid },
  { to: "/einstellungen", label: "Einstellungen", Icon: Cog6ToothIcon, IconActive: CogSolid },
];

export function AppShell() {
  const { data: me } = useMe();
  const logout = useLogout();

  return (
    <div className="min-h-full bg-ios-bg">
      {/* Top Bar (mobile) — sticky glass */}
      <header className="md:hidden sticky top-0 z-30 glass border-b border-ios-line/60 pt-safe">
        <div className="h-12 px-4 flex items-center justify-between">
          <h1 className="text-[17px] font-semibold tracking-tight">Quittungen</h1>
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-ios-muted">{me?.username}</span>
            <button
              type="button"
              onClick={() => logout.mutate()}
              aria-label="Abmelden"
              title="Abmelden"
              className="-mr-1 p-1 text-ios-muted hover:text-ios-ink active:opacity-60"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar (Desktop) — nur Navigation, kein Tree */}
        <aside className="hidden md:flex w-64 flex-col border-r border-ios-line/60 bg-white/40 sticky top-0 h-screen px-4 py-6">
          <div className="px-2 mb-6">
            <h1 className="text-[22px] font-semibold tracking-tight">Quittungen</h1>
            <p className="text-[13px] text-ios-muted mt-0.5">{me?.username}</p>
          </div>
          <nav className="space-y-1 mb-4 flex-1">
            {NAV.map(({ to, label, Icon, IconActive }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/inbox"}
                className={({ isActive }) =>
                  clsx(
                    "flex items-center gap-3 px-3 py-2 rounded-ios text-[15px] transition",
                    isActive
                      ? "bg-ios-blue/10 text-ios-blue font-medium"
                      : "text-ios-ink hover:bg-ios-line/40",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <IconActive className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5 text-ios-muted" />
                    )}
                    <span>{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="flex items-center gap-3 px-3 py-2 rounded-ios text-[15px] text-ios-muted hover:bg-ios-line/40 transition"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span>Abmelden</span>
          </button>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 pb-24 md:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Bottom Tab Bar (mobile) — glass, 5 Navigationsziele */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-ios-line/60 pb-safe">
        <div className="grid grid-cols-6">
          {NAV.filter((n) => !n.nurDesktop).map(({ to, label, Icon, IconActive }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/inbox"}
              className={({ isActive }) =>
                clsx(
                  "flex flex-col items-center justify-center gap-1 py-2 text-[9px] transition min-w-0",
                  isActive ? "text-ios-blue" : "text-ios-muted",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive ? <IconActive className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  <span className="w-full truncate text-center px-0.5">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
